/**
 * 画面下端に貼り付く要素（sticky/fixed のフッター）の高さを、アプリ全体の
 * フローティング要素へ知らせる（CSS 変数 `--app-bottom-bar-height`）。
 *
 * ⚠️ **z-index の上げ下げで解決しない**。画面下端に貼る要素は複数あり
 * （実行状況カード・ページのフッター）、上げれば相手を隠し、下げれば自分が隠れる。
 * どちらも「隠れて操作できない」ままなので、**重ならない位置に逃がす**。
 *
 * 使う側：下端に貼るフッターを持つ画面が、そのフッターの ref を渡す。
 * 受ける側：フローティング要素が `calc(... + var(--app-bottom-bar-height, 0px))` で位置を決める。
 * 変数が無い画面では 0 に落ちるので、他画面の見た目は変わらない。
 */
import { useEffect, type RefObject } from 'react'

/** 画面下端に貼り付くフッターの高さ。 */
export const BOTTOM_BAR_HEIGHT_VAR = '--app-bottom-bar-height'

/**
 * 実行状況カード（`CombinedProgressIndicator`）の高さ。
 *
 * ⚠️ **カードは行数で高さが変わる**（プロンプト生成／分析／検知の3行が出入りする）。
 * 高さを定数で持つと、行が増減したとき静かにズレて重なる。実測して知らせる。
 */
export const PROGRESS_INDICATOR_HEIGHT_VAR = '--app-progress-indicator-height'

export function useBottomBarOffset(
  ref: RefObject<HTMLElement>,
  cssVariable: string = BOTTOM_BAR_HEIGHT_VAR,
  /**
   * 対象要素がいま描画されているか。
   * ⚠️ **ref の中身が null になっても effect は再実行されない**（ref の変化は再描画を起こさない）。
   * 条件付きで消える要素に使うときは、その条件をここへ渡すこと。渡さないと**消えた後も
   * 最後の高さが残り**、フローティング要素が宙に浮く。
   */
  enabled: boolean = true
): void {
  useEffect(() => {
    const root = document.documentElement
    const element = enabled ? ref.current : null
    if (!element) {
      root.style.removeProperty(cssVariable)
      return
    }

    // 高さは可変（狭い画面では折り返して2段になる）ため、都度測って書き戻す。
    const update = () => root.style.setProperty(cssVariable, `${element.offsetHeight}px`)
    update()

    // ResizeObserver が無い環境（一部のテスト環境）では初回の測定だけで足りる。
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null
    observer?.observe(element)

    return () => {
      observer?.disconnect()
      // ⚠️ 画面を離れたら必ず消す。残すと、フッターの無い画面でフローティング要素が
      // 宙に浮いたままになる。
      root.style.removeProperty(cssVariable)
    }
  }, [ref, cssVariable, enabled])
}
